import { AnswerResults, DepartmentType, Question, QuestionId } from '@/types/questions'

const BRANCH_RESULT_TYPES = [AnswerResults.SHOW_NEXT_QUESTION, AnswerResults.EXTEND_QUESTION_TEXT]

const getBranchTargets = (question: Question): QuestionId[] =>
    question.questionContent.answers
        .filter((answer) => BRANCH_RESULT_TYPES.includes(answer.result.type) && answer.result.target)
        .map((answer) => answer.result.target as QuestionId)

/**
 * Maps each question to the department declared on the Q1 answer that opens its subtree.
 * Q1 itself is not tagged. If a question is reachable from both departments, the later
 * Q1 answer overwrites (trees are expected to be disjoint).
 */
export const getDepartmentTypeByQuestionId = (questions: Question[]): Record<QuestionId, DepartmentType> => {
    const departmentTypeByQuestionId: Record<QuestionId, DepartmentType> = {}
    if (!questions.length) {
        return departmentTypeByQuestionId
    }

    const questionsById = new Map(questions.map((question) => [question.id, question]))
    const rootQuestion = questions[0]

    const assignDepartmentToSubtree = (startQuestionId: QuestionId, departmentType: DepartmentType) => {
        const walkQueue: QuestionId[] = [startQuestionId]
        const visitedQuestionIds = new Set<QuestionId>()

        for (let queueIndex = 0; queueIndex < walkQueue.length; queueIndex++) {
            const questionId = walkQueue[queueIndex]
            if (visitedQuestionIds.has(questionId)) {
                continue
            }

            visitedQuestionIds.add(questionId)
            departmentTypeByQuestionId[questionId] = departmentType

            const question = questionsById.get(questionId)
            if (!question) {
                continue
            }

            getBranchTargets(question).forEach((targetQuestionId) => {
                if (!visitedQuestionIds.has(targetQuestionId)) {
                    walkQueue.push(targetQuestionId)
                }
            })
        }
    }

    rootQuestion.questionContent.answers.forEach((answer) => {
        const departmentType = answer.result.departmentType
        const targetQuestionId = answer.result.target
        if (!departmentType || !targetQuestionId) {
            return
        }

        assignDepartmentToSubtree(targetQuestionId, departmentType)
    })

    return departmentTypeByQuestionId
}
